import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CogIcon, 
  ChartPieIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  HomeIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  DocumentPlusIcon,
  BriefcaseIcon,
  UsersIcon,
  MapIcon,
  RocketLaunchIcon,
  PresentationChartLineIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
  BookOpenIcon,
  ShoppingBagIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import {
  MagnifyingGlassIcon as MagnifyingGlassIconOutline,
  PlusIcon as PlusIconOutline,
  SwatchIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useTheme } from '@/state/theme/ThemeProvider';
import { unifiedRBACService, NavigationModule } from '@/services/unifiedRBACService';
import { iconService } from '@/services/iconService';
import { CommandPalette } from '@/components/navigation/CommandPalette';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import api from '@/services/api';
import { authService } from '@/services/auth';
import tmosLogo from '@/assets/images/brand/tmos-logo.png';
import { PePortfolioViewContextSelect } from '@/components/pe-portfolio/PePortfolioViewContextSelect';
import { PE_DELEGATED_SHELL_UI_ENABLED } from '@/services/peDelegatedContext';

interface HorizontalNavigationProps {
  onMobileMenuToggle?: () => void;
  onSidebarToggle?: () => void;
}

export const HorizontalNavigation: React.FC<HorizontalNavigationProps> = ({
  onMobileMenuToggle,
  onSidebarToggle
}) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(null);
  const [navigationModules, setNavigationModules] = useState<NavigationModule[]>([]);
  const [isLoadingNavigation, setIsLoadingNavigation] = useState(true);
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [userNavPreferences, setUserNavPreferences] = useState<{
    top_navbar: any[];
    quick_actions: any[];
  } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, isLoading, setUser } = useAuth();
  const { t, currentLanguage, setLanguage, availableLanguages } = useLanguage();
  const { currentTheme, setTheme, availableThemes } = useTheme();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const themeRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  /** Match provisioning template bar: outline stroke + theme accent (single color, not emoji). */
  const navMonoIconClass =
    'shrink-0 text-[var(--color-info)] [stroke:currentColor]';

  // Handle Cmd+K / Ctrl+K shortcut for Command Palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K on Mac, Ctrl+K on Windows/Linux
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Load user navigation preferences
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!isAuthenticated || isLoading || !user?.id) {
        return;
      }

      try {
        const response = await api.get('/navigation/user/preferences');
        setUserNavPreferences(response.data.data);
      } catch (error: any) {
        // Silently fail - user preferences are optional
        console.debug('Could not load user navigation preferences:', error);
        setUserNavPreferences(null);
      }
    };

    loadUserPreferences();
  }, [isAuthenticated, isLoading, user?.id]);

  // Save user preference to backend
  const saveUserPreference = async (key: string, value: string) => {
    if (!user?.id) return;
    
    try {
      // Get current profile to ensure we have firstName and lastName (required by backend)
      const profileResponse = await api.get('/users/profile');
      const profile = profileResponse.data.data;
      
      // Update preference via profile update endpoint
      await api.put('/users/profile', {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone || '',
        title: profile.title || '',
        preferences: {
          [key]: value
        }
      });
    } catch (error) {
      // Silently fail - preference saving is not critical
      console.debug('Failed to save user preference:', error);
    }
  };

  // Helper function to render icon (outline heroicons, monocolor like provisioning bar)
  const renderIcon = (iconName: string | undefined) => {
    if (!iconName) {
      const Fallback = iconService.getFallbackIcon();
      return <Fallback className={`h-5 w-5 ${navMonoIconClass}`} />;
    }

    const isEmoji = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(iconName);

    if (isEmoji) {
      const emojiToIcon: Record<string, React.ComponentType<{ className?: string }>> = {
        '🛡️': ShieldCheckIcon,
        '🚨': ExclamationTriangleIcon,
        '✅': CheckCircleIcon,
        '🚀': RocketLaunchIcon,
        '📄': PresentationChartLineIcon,
        '📦': ArchiveBoxIcon,
        '📚': BookOpenIcon,
        '🛒': ShoppingBagIcon,
        '🛍️': ShoppingBagIcon,
        '💬': ChatBubbleLeftRightIcon,
      };
      const Mapped = emojiToIcon[iconName];
      if (Mapped) {
        return <Mapped className={`h-5 w-5 ${navMonoIconClass}`} />;
      }
      return <span className="text-xl opacity-90">{iconName}</span>;
    }

    const IconComponent = iconService.getIcon(iconName);
    return <IconComponent className={`h-5 w-5 ${navMonoIconClass}`} />;
  };

  /** Top nav / quick-action row: prefer custom label, else DB translation_key, else display name. */
  const resolvePreferenceLabel = useCallback(
    (pref: {
      custom_label?: string | null;
      screen_display_name?: string;
      screen_translation_key?: string;
      screenTranslationKey?: string;
    }) => {
      if (pref.custom_label?.trim()) return pref.custom_label;
      const tk = pref.screen_translation_key ?? pref.screenTranslationKey;
      if (tk?.trim()) {
        const tr = t(tk);
        if (tr !== tk) return tr;
      }
      return pref.screen_display_name || t('navigation.unknownScreen');
    },
    [t]
  );

  // Top Navbar items - use user preferences if available, otherwise fallback to defaults
  const topNavbarItems = useMemo(() => {
    if (userNavPreferences && userNavPreferences.top_navbar && userNavPreferences.top_navbar.length > 0) {
      // Use user-configured top navbar items
      return userNavPreferences.top_navbar
        .filter(pref => pref.is_active !== false) // Only show active items
        .sort((a, b) => a.display_order - b.display_order)
        .map(pref => {
          const iconName = pref.custom_icon || pref.screen_icon || '📄';
          return {
            label: resolvePreferenceLabel(pref),
            icon: renderIcon(iconName),
            path: pref.screen_route_path || '/dashboard',
          };
        });
    }

    // Fallback to default top navbar items - use database icons
    return [
      {
        label: t('navigation.menuItems.risks') || 'Risks',
        icon: renderIcon('🛡️'), // Risk Management icon
        path: '/risk',
      },
      {
        label: t('navigation.menuItems.issue_management') || 'Issues',
        icon: renderIcon('🚨'), // Issue Management icon
        path: '/issues',
      },
      {
        label: t('navigation.menuItems.action_management') || 'Actions',
        icon: renderIcon('✅'), // Actions icon
        path: '/actions',
      },
    ];
  }, [userNavPreferences, t, resolvePreferenceLabel]);

  // Quick Actions menu items - use user preferences if available, otherwise fallback to defaults
  const quickActions = useMemo(() => {
    if (userNavPreferences && userNavPreferences.quick_actions.length > 0) {
      // Use user-configured quick actions
      return userNavPreferences.quick_actions
        .sort((a, b) => a.display_order - b.display_order)
        .map(pref => {
          const iconName = pref.custom_icon || pref.screen_icon || '📄';
          return {
            label: resolvePreferenceLabel(pref),
            icon: renderIcon(iconName),
            action: () => {
              navigate(pref.screen_route_path || '/dashboard');
              setIsQuickActionsOpen(false);
            },
          };
        });
    }

    // Fallback to default quick actions - use database icons if available
    // These will be fetched from navigation_screens table
    const defaultActions = [
      {
        label: t('navigation.quickActions.newRisk') || 'New Risk',
        iconName: 'shield-check',
        path: '/risk?tab=overview&create=true',
      },
      {
        label: t('navigation.quickActions.newIssue') || 'New Issue',
        iconName: 'exclamation-triangle',
        path: '/issues/create',
      },
      {
        label: t('navigation.quickActions.newAction') || 'New Action',
        iconName: 'check-circle',
        path: '/actions',
      },
      {
        label: t('navigation.quickActions.newTransformation') || 'New Transformation',
        iconName: 'rocket',
        path: '/transformations/create',
      },
    ];

    return defaultActions.map(action => ({
      label: action.label,
      icon: renderIcon(action.iconName),
      action: () => {
        navigate(action.path);
        setIsQuickActionsOpen(false);
      },
    }));
  }, [userNavPreferences, navigate, t, resolvePreferenceLabel]);

  // Check if user has admin access (any ADMIN category items)
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!isAuthenticated || isLoading || !user?.id) {
        setHasAdminAccess(false);
        return;
      }

      try {
        // Get auth token
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        
        if (!token) {
          console.warn('[Admin Access Check] No auth token found');
          setHasAdminAccess(false);
          return;
        }

        // First, check if user is Platform Administrator (quick check)
        try {
          const rolesResponse = await fetch(`/api/v1/users/${user.id}/roles`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (rolesResponse.ok) {
            // Check if response is actually JSON before parsing
            const contentType = rolesResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const rolesData = await rolesResponse.json();
              const isPlatformAdmin = rolesData.data?.some((role: any) => 
                role.role_name === 'Platform Administrator'
              ) || false;
              
              if (isPlatformAdmin) {
                console.log('[Admin Access Check] User is Platform Administrator - granting admin access');
                setHasAdminAccess(true);
                return;
              }
            } else {
              console.warn('[Admin Access Check] Roles endpoint returned non-JSON response:', contentType);
            }
          }
        } catch (rolesError) {
          console.warn('[Admin Access Check] Could not check roles, continuing with navigation check:', rolesError);
        }

        // If not Platform Admin, check if user has access to any admin navigation items
        const response = await fetch(`/api/v1/rbac/users/${user.id}/navigation-permissions?category=ADMIN`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Check if response is actually JSON before parsing
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            
            // User has admin access if there are any modules with permitted screens
            const hasAccess = 
              data.data?.modules?.some((module: any) => 
                module.children?.some((child: any) => 
                  'screen_code' in child && child.has_read_permission === true
                )
              ) || false;
            
            setHasAdminAccess(hasAccess);
          } else {
            console.warn('[Admin Access Check] Navigation endpoint returned non-JSON response:', contentType);
            setHasAdminAccess(false);
          }
        } else {
          console.error('[Admin Access Check] Navigation API error:', response.status, response.statusText);
          setHasAdminAccess(false);
        }
      } catch (error) {
        console.error('[Admin Access Check] Error:', error);
        setHasAdminAccess(false);
      }
    };

    checkAdminAccess();
  }, [isAuthenticated, isLoading, user?.id]);

  // Handle click outside Quick Actions dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target as Node)) {
        setIsQuickActionsOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(event.target as Node)) {
        setIsThemeOpen(false);
      }
      if (languageRef.current && !languageRef.current.contains(event.target as Node)) {
        setIsLanguageOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    if (isQuickActionsOpen || isThemeOpen || isLanguageOpen || activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isQuickActionsOpen, isThemeOpen, isLanguageOpen, activeDropdown]);

  // Recursive function to filter out Quick Access items from all navigation levels
  const filterQuickAccessItems = (items: any[]): any[] => {
    return items.map(item => {
      if (item.children && item.children.length > 0) {
        // Recursively filter children
        const filteredChildren = filterQuickAccessItems(item.children);
        return {
          ...item,
          children: filteredChildren
        };
      }
      return item;
    }).filter(item => {
      // Filter out items marked for Quick Access
      if ('screen_code' in item) {
        return !item.quick_access;
      }
      return true;
    });
  };

  const handleSignOut = () => {
    logout();
  };

  // Get profile picture URL (from user preferences or null)
  const getProfilePictureUrl = () => {
    const imageUrl = (user as any)?.profileImageUrl || (user as any)?.preferences?.profile_image_url || null;
    if (!imageUrl) return null;
    
    // Build full URL if it's a relative path (development)
    // The path from backend is like /uploads/filename.jpg, and we serve it at /uploads
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
    const imagePath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    // URL encode each path segment to handle spaces and special characters (but keep slashes)
    const pathParts = imagePath.split('/');
    const encodedParts = pathParts.map((part: string) => part ? encodeURIComponent(part) : '');
    const encodedPath = encodedParts.join('/');
    return `${apiUrl}${encodedPath}`;
  };

  // Refresh user data when component mounts to ensure profile picture is up to date
  useEffect(() => {
    const refreshUserData = async () => {
      if (!isAuthenticated || isLoading || !user?.id || !setUser) {
        return;
      }

      try {
        // Refresh user data to get latest profile picture
        const userData = await authService.getCurrentUser();
        if (userData) {
          setUser(userData);
        }
      } catch (error) {
        // Silently fail - user data refresh is not critical
        console.debug('Failed to refresh user data:', error);
      }
    };

    // Only refresh if user doesn't have profileImageUrl (to avoid unnecessary API calls)
    if (user && !(user as any).profileImageUrl) {
      refreshUserData();
    }
  }, [isAuthenticated, isLoading, user?.id, setUser]);

  // Load hierarchical navigation from database
  useEffect(() => {
    const loadNavigation = async () => {
      if (!isAuthenticated || isLoading || !user?.id) {
        setNavigationModules([]);
        setIsLoadingNavigation(false);
        return;
      }

      try {
        setIsLoadingNavigation(true);
        setNavigationError(null);
        // Get user's navigation tree from unified RBAC service
        const navigationTree = await unifiedRBACService.getCurrentUserNavigation();
        


        // Check if navigation tree has modules
        if (!navigationTree || !navigationTree.modules || navigationTree.modules.length === 0) {
          const errorMessage = `No functionality available. User ${user.email} has no role assignments or permissions. Please contact your administrator to assign appropriate roles.`;
          console.error('[Navigation Error]', errorMessage);
          setNavigationError(errorMessage);
          setNavigationModules([]);
          return;
        }

        // Filter and set navigation modules from the tree
        // Note: Location-based filtering removed - we only use sidebar navigation now
        const filteredModules = filterActiveItems(navigationTree.modules);
        
        
        setNavigationModules(filteredModules);
      } catch (error: any) {
        const errorMessage = `Unable to load navigation. Please contact your administrator if this issue persists. (Error: ${error.message})`;
        console.error('[Navigation Error]', errorMessage);
        console.error('[Navigation Error Details]', {
          userId: user.id,
          userEmail: user.email,
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url,
          stack: error.stack
        });
        
        setNavigationError(errorMessage);
        setNavigationModules([]);
      } finally {
        setIsLoadingNavigation(false);
      }
    };
    loadNavigation();
  }, [isAuthenticated, isLoading, user?.id]);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter out inactive modules and screens
  const filterActiveItems = (modules: NavigationModule[]): NavigationModule[] => {
    return modules
      .filter(module => module.is_active !== false) // Keep active modules
      .map(module => ({
        ...module,
        children: module.children
          .filter(child => {
            // Filter out inactive screens
            if ('screen_code' in child) {
              return child.is_active !== false;
            }
            // For sub-modules, recursively filter their children
            if ('module_code' in child && child) {
              const filteredSubModule = {
                ...(child as any),
                children: (child as any).children?.filter((subChild: any) => 
                  'screen_code' in subChild ? subChild.is_active !== false : true
                ) || []
              };
              return filteredSubModule.children.length > 0 || filteredSubModule.is_active !== false;
            }
            return true;
          })
      }))
      .filter(module => module.children.length > 0 || module.is_active !== false); // Keep modules with active children or active themselves
  };

  // Map database display_name to translation key
  const getTranslationKey = (displayName: string, translationKey?: string): string => {
    // Use translation_key from database if available
    if (translationKey) {
      return translationKey;
    }
    
    // If no translation_key provided, return the display_name as fallback
    // This will show the raw display_name instead of a broken key
    console.warn(`No translation_key provided for display_name: ${displayName}`);
    return displayName;
  };

  // Memoized translation function to ensure re-rendering when language changes
  const getTranslatedText = useMemo(() => {
    return (displayName: string, translationKey?: string) => {
      const key = getTranslationKey(displayName, translationKey);
      const translatedText = t(key);
      return translatedText === key ? displayName : translatedText;
    };
  }, [t]);

  // Get appropriate icon for navigation item with robust fallback system
  const getIconForPath = (path: string, icon?: string) => {
    if (icon) {
      // Use robust icon service that never breaks the system
      return iconService.getIcon(icon);
    }

    // Fallback icon mapping based on path
    const pathKey = path.split('/').pop() || 'default';
    const fallbackIconMap: Record<string, React.ComponentType<any>> = {
      'dashboard': HomeIcon,
      'users': UsersIcon,
      'tenants': BuildingOfficeIcon,
      'kpis': ChartPieIcon,
      'transformations': RocketLaunchIcon,
      'processes': CogIcon,
      'reports': PresentationChartLineIcon,
      'admin': ShieldCheckIcon,
      'marketplace': MapIcon,
      'roadmaps': MapIcon,
      'create': DocumentPlusIcon,
      'tenant': BriefcaseIcon,
      'default': PresentationChartLineIcon
    };
    return fallbackIconMap[pathKey] || fallbackIconMap['default'];
  };

  if (isLoadingNavigation) {
    return (
      <nav className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="animate-pulse text-sm">{t('navigation.loadingNavigation')}</div>
        </div>
      </nav>
    );
  }

  // Show error message if navigation failed to load, but still allow user controls
  if (navigationError) {
    return (
      <nav className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-red-300 bg-red-900 bg-opacity-50 px-3 py-1 rounded">
              ⚠️ {t('navigation.noFunctionalityAvailable')}
            </div>
          </div>
          
          {/* Right side - Language and User (always available) */}
          <div className="flex items-center space-x-3">
            {/* Language Selector */}
            <div ref={languageRef} className="relative">
              <button
                onClick={() => {
                  setIsLanguageOpen(!isLanguageOpen);
                  setActiveDropdown(null);
                }}
                className="flex items-center justify-center space-x-1 px-3 py-2 border border-[var(--color-border)] rounded-md text-sm text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
                title={t('navigation.top.changeLanguage')}
              >
                <span className="text-base">{currentLanguage.flag}</span>
              </button>

              {isLanguageOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsLanguageOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-[var(--color-secondary-bg)] border border-[var(--color-border)] rounded-md shadow-lg z-20">
                    <div className="py-1">
                      {availableLanguages.map((lang) => (
                        <button
                          key={lang.id}
                          onClick={async () => {
                            setLanguage(lang.id);
                            setIsLanguageOpen(false);
                            // Save language preference to backend
                            await saveUserPreference('language', lang.id);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors ${
                            currentLanguage.id === lang.id
                              ? 'bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]'
                              : 'text-[var(--color-secondary-text)] hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)]'
                          }`}
                        >
                          <span className="text-base">{lang.flag}</span>
                          <span className="flex-1 text-sm font-medium">{lang.id.split('-')[0].toUpperCase()}</span>
                          {currentLanguage.id === lang.id && (
                            <div className="w-2 h-2 rounded-full bg-[var(--color-success)]" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => {
                  setActiveDropdown(activeDropdown === 'user' ? null : 'user');
                  setIsLanguageOpen(false);
                }}
                className="flex items-center space-x-2 px-2 py-1 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
                title={t('navigation.profile')}
              >
                {getProfilePictureUrl() ? (
                  <img
                    src={getProfilePictureUrl()}
                    alt={`${user?.firstName} ${user?.lastName}`}
                    className="w-8 h-8 rounded-full object-cover border border-[var(--color-border)]"
                  />
                ) : (
                  <UserCircleIcon className="w-8 h-8" />
                )}
                <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
              </button>

              {activeDropdown === 'user' && (
                <>
                  {/* Invisible bridge to prevent gap closure */}
                  <div className="absolute right-0 top-full w-full h-2" />
                  <div 
                    className="absolute top-full right-0 mt-1 w-48 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded-md shadow-lg z-50 border border-[var(--color-border)]"
                    onMouseEnter={() => setActiveDropdown('user')}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                  <div className="p-4 border-b border-[var(--color-border)]">
                    <div className="flex items-center space-x-3">
                      {getProfilePictureUrl() ? (
                        <img
                          src={getProfilePictureUrl()}
                          alt={`${user?.firstName} ${user?.lastName}`}
                          className="w-10 h-10 rounded-full object-cover border border-[var(--color-border)]"
                        />
                      ) : (
                        <UserCircleIcon className="w-10 h-10 text-[var(--color-secondary-text)]" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[var(--color-secondary-text)] truncate">
                          {user?.firstName} {user?.lastName}
                        </div>
                        <div className="text-xs text-[var(--color-secondary-text)] opacity-75 truncate">
                          {user?.email}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="py-2">
                    <Link
                      to="/settings/profile"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('navigation.personalInformation') || 'Personal information'}
                    </Link>
                    <Link
                      to="/settings/profile?section=business"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('navigation.businessInformation') || 'Business information'}
                    </Link>
                    <Link
                      to="/settings/profile?section=subscription"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('navigation.subscription') || 'Subscription'}
                    </Link>
                    <Link
                      to="/settings/profile?section=preferences"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('navigation.preferences') || 'Preferences'}
                    </Link>
                    <div className="border-t border-[var(--color-border)] my-1" />
                    <Link
                      to="/privacy"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('website.footer.privacy_policy') || 'Privacy Policy'}
                    </Link>
                    <Link
                      to="/terms"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('website.footer.terms_of_service') || 'Terms of Service'}
                    </Link>
                    <a
                      href="mailto:info@t-mos.com"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('website.footer.contact') || 'Contact Us'}
                    </a>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                    >
                      {t('navigation.signOut')}
                    </button>
                  </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
    );
  }


  return (
    <>
      {/* Desktop Navigation */}
      <nav className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 shadow-sm hidden md:block" ref={dropdownRef}>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-4">
            {/* Hamburger Menu Button - Toggle Sidebar */}
            {onSidebarToggle && (
              <button
                onClick={onSidebarToggle}
                className="p-2 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
                aria-label={t('navigation.top.toggleSidebar')}
                title={t('navigation.top.toggleSidebar')}
              >
                <Bars3Icon className="w-6 h-6" />
              </button>
            )}
            
            {/* T-MOS Brand */}
            <Link to="/" className="flex items-center">
              <img
                src={tmosLogo}
                alt={t('navigation.logoAlt')}
                className="h-10 w-auto"
              />
            </Link>

            {PE_DELEGATED_SHELL_UI_ENABLED ? <PePortfolioViewContextSelect /> : null}

            {/* Module Navigation Removed - All navigation moved to sidebar and Command Palette */}
            {/* Users access navigation via: */}
            {/* 1. Sidebar (hamburger menu) */}
            {/* 2. Command Palette (Cmd+K / Search button) */}
            {/* 3. Universal nav items (Risks, Issues, Actions) */}
          </div>

          {/* Right side - Search, Universal Nav Items, Quick Actions, Language, User */}
          <div className="flex items-center space-x-2">
            {/* Search / Command Palette Trigger - Moved to left of all navbar actions */}
            <button
              onClick={() => setIsCommandPaletteOpen(true)}
              className="hidden md:flex items-center space-x-2 px-3 py-2 border border-[var(--color-border)] rounded-md text-sm text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
              title={t('navigation.top.searchShortcutTitle')}
            >
              <MagnifyingGlassIconOutline className="w-4 h-4" />
              <span className="hidden lg:inline">{t('navigation.top.search')}</span>
              <div className="hidden lg:flex items-center space-x-1 text-xs text-[var(--color-primary-text)] opacity-75">
                <kbd className="px-1.5 py-0.5 bg-[var(--color-secondary-bg)] rounded">⌘</kbd>
                <span>K</span>
              </div>
            </button>

            {/* Universal Navigation Items: Dynamic from user preferences */}
            <nav className="hidden lg:flex items-center space-x-1 mr-2">
              {topNavbarItems.map((item, index) => {
                return (
                  <Link
                    key={index}
                    to={item.path}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-1 ${
                      isActive(item.path)
                        ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]'
                        : 'text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]'
                    }`}
                  >
                    <span className="flex items-center justify-center">{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Quick Actions Dropdown */}
            <div 
              className="relative" 
              ref={quickActionsRef}
              onMouseEnter={() => setIsQuickActionsOpen(true)}
              onMouseLeave={() => setIsQuickActionsOpen(false)}
            >
              <button
                onClick={() => setIsQuickActionsOpen(!isQuickActionsOpen)}
                className="flex items-center space-x-1 px-3 py-2 border border-[var(--color-border)] rounded-md text-sm text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
                title={t('navigation.quickActions.title')}
              >
                <PlusIconOutline className="w-4 h-4" />
                <span className="hidden lg:inline">{t('navigation.quickActions.title') || 'Quick Actions'}</span>
                <ChevronDownIcon className="w-3 h-3" />
              </button>

              {/* Quick Actions Menu */}
              {isQuickActionsOpen && (
                <>
                  {/* Invisible bridge to prevent gap closure */}
                  <div className="absolute right-0 top-full w-full h-2" />
                  <div 
                    className="absolute right-0 mt-2 w-56 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded-md shadow-lg border border-[var(--color-border)] z-50"
                    onMouseEnter={() => setIsQuickActionsOpen(true)}
                    onMouseLeave={() => setIsQuickActionsOpen(false)}
                  >
                  <div className="py-1">
                    {quickActions.map((action, index) => {
                      return (
                        <button
                          key={index}
                          onClick={action.action}
                          className="w-full text-left px-4 py-2 text-sm text-[var(--color-secondary-text)] hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] flex items-center space-x-2"
                        >
                          <span className="flex items-center justify-center w-4 h-4">{action.icon}</span>
                          <span>{action.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  </div>
                </>
              )}
            </div>

            {/* Notification Bell */}
            <NotificationBell />

            {/* Theme Selector */}
            <div 
              ref={themeRef} 
              className="relative"
              onMouseEnter={() => {
                setIsThemeOpen(true);
                setIsLanguageOpen(false);
                setActiveDropdown(null);
              }}
              onMouseLeave={() => setIsThemeOpen(false)}
            >
              <button
                onClick={() => {
                  setIsThemeOpen(!isThemeOpen);
                  setIsLanguageOpen(false);
                  setActiveDropdown(null);
                }}
                className="flex items-center justify-center px-3 py-2 border border-[var(--color-border)] rounded-md text-sm text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
                title={t('navigation.chooseTheme')}
              >
                <SwatchIcon className="w-5 h-5" />
              </button>

              {isThemeOpen && (
                <>
                  {/* Invisible bridge to prevent gap closure */}
                  <div className="absolute right-0 top-full w-full h-2" />
                  <div 
                    className="absolute right-0 mt-2 w-64 bg-[var(--color-secondary-bg)] border border-[var(--color-border)] rounded-md shadow-lg z-20"
                    onMouseEnter={() => setIsThemeOpen(true)}
                    onMouseLeave={() => setIsThemeOpen(false)}
                  >
                  <div className="p-2">
                    <h3 className="text-sm font-medium text-[var(--color-secondary-text)] mb-2 px-2">
                      {t('navigation.chooseTheme') || 'Choose Theme'}
                    </h3>
                    <div className="space-y-1 max-h-80 overflow-y-auto">
                      {availableThemes.map((theme) => (
                        <button
                          key={theme.id}
                          onClick={async () => {
                            setTheme(theme.id);
                            setIsThemeOpen(false);
                            // Save theme preference to backend
                            await saveUserPreference('theme', theme.id);
                          }}
                          className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm transition-colors ${
                            currentTheme.id === theme.id
                              ? 'bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]'
                              : 'text-[var(--color-secondary-text)] hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)]'
                          }`}
                        >
                          <div
                            className="w-4 h-4 rounded-full border-2 border-[var(--color-border)]"
                            style={{ backgroundColor: theme.colors.primary.background }}
                          />
                          <div className="flex-1 text-left">
                            <div className="font-medium">{theme.name}</div>
                          </div>
                          {currentTheme.id === theme.id && (
                            <div className="w-2 h-2 rounded-full bg-[var(--color-success)]" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  </div>
                </>
              )}
            </div>

            {/* Language Selector */}
            <div 
              ref={languageRef} 
              className="relative"
              onMouseEnter={() => {
                setIsLanguageOpen(true);
                setIsThemeOpen(false);
                setActiveDropdown(null);
              }}
              onMouseLeave={() => setIsLanguageOpen(false)}
            >
              <button
                onClick={() => {
                  setIsLanguageOpen(!isLanguageOpen);
                  setIsThemeOpen(false);
                  setActiveDropdown(null);
                }}
                className="flex items-center justify-center space-x-1 px-3 py-2 border border-[var(--color-border)] rounded-md text-sm text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
                title={t('navigation.top.changeLanguage')}
              >
                <span className="text-base">{currentLanguage.flag}</span>
              </button>

              {isLanguageOpen && (
                <>
                  {/* Invisible bridge to prevent gap closure */}
                  <div className="absolute right-0 top-full w-full h-2" />
                  <div 
                    className="absolute right-0 mt-2 w-56 bg-[var(--color-secondary-bg)] border border-[var(--color-border)] rounded-md shadow-lg z-20"
                    onMouseEnter={() => setIsLanguageOpen(true)}
                    onMouseLeave={() => setIsLanguageOpen(false)}
                  >
                  <div className="py-1">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.id}
                        onClick={async () => {
                          setLanguage(lang.id);
                          setIsLanguageOpen(false);
                          // Save language preference to backend
                          await saveUserPreference('language', lang.id);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors ${
                          currentLanguage.id === lang.id
                            ? 'bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]'
                            : 'text-[var(--color-secondary-text)] hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)]'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span className="flex-1 text-sm font-medium">{lang.id.split('-')[0].toUpperCase()}</span>
                        {currentLanguage.id === lang.id && (
                          <div className="w-2 h-2 rounded-full bg-[var(--color-success)]" />
                        )}
                      </button>
                    ))}
                  </div>
                  </div>
                </>
              )}
            </div>

            {/* User Menu */}
            <div 
              className="relative" 
              ref={profileRef}
              onMouseEnter={() => {
                setActiveDropdown('user');
                setIsThemeOpen(false);
                setIsLanguageOpen(false);
              }}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                onClick={() => {
                  setActiveDropdown(activeDropdown === 'user' ? null : 'user');
                  setIsThemeOpen(false);
                  setIsLanguageOpen(false);
                }}
                className="flex items-center space-x-2 px-2 py-1 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
                title={t('navigation.profile')}
              >
                {getProfilePictureUrl() ? (
                  <img
                    src={getProfilePictureUrl()}
                    alt={`${user?.firstName} ${user?.lastName}`}
                    className="w-8 h-8 rounded-full object-cover border border-[var(--color-border)]"
                  />
                ) : (
                  <UserCircleIcon className="w-8 h-8" />
                )}
                <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
              </button>

              {activeDropdown === 'user' && (
                <>
                  {/* Invisible bridge to prevent gap closure */}
                  <div className="absolute right-0 top-full w-full h-2" />
                  <div 
                    className="absolute top-full right-0 mt-1 w-48 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded-md shadow-lg z-50 border border-[var(--color-border)]"
                    onMouseEnter={() => setActiveDropdown('user')}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                  <div className="py-2">
                    <Link
                      to="/settings/profile"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('navigation.profile')}
                    </Link>
                    {/* Admin link in user menu if available */}
                    {hasAdminAccess && (
                      <>
                        <Link
                          to="/admin/dashboard"
                          className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm flex items-center space-x-2"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <CogIcon className="w-4 h-4" />
                          <span>{t('navigation.admin') || 'Administration'}</span>
                        </Link>
                        <div className="border-t border-[var(--color-border)] my-1" />
                      </>
                    )}
                    <div className="border-t border-[var(--color-border)] my-1" />
                    <Link
                      to="/privacy"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('website.footer.privacy_policy') || 'Privacy Policy'}
                    </Link>
                    <Link
                      to="/terms"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('website.footer.terms_of_service') || 'Terms of Service'}
                    </Link>
                    <a
                      href="mailto:info@t-mos.com"
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                      onClick={() => setActiveDropdown(null)}
                    >
                      {t('website.footer.contact') || 'Contact Us'}
                    </a>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] transition-colors text-sm"
                    >
                      {t('navigation.signOut')}
                    </button>
                  </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] px-4 py-2 shadow-sm md:hidden">
        <div className="flex items-center justify-between gap-2">
          {/* Left: Hamburger Menu Button */}
          <button
            onClick={() => {
              if (onMobileMenuToggle) {
                onMobileMenuToggle();
              } else {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }}
            className="p-2 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors flex-shrink-0"
          >
            {mobileMenuOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>

          {/* Center: Top Navbar Items - Icons Only on Mobile */}
          <div className="flex items-center gap-1 flex-1 overflow-x-auto scrollbar-hide">
            {topNavbarItems.map((item, index) => {
              return (
                <Link
                  key={index}
                  to={item.path}
                  className={`p-2 rounded-md transition-colors flex items-center justify-center flex-shrink-0 ${
                    isActive(item.path)
                      ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]'
                      : 'text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]'
                  }`}
                  title={item.label}
                >
                  <span className="flex items-center justify-center">{item.icon}</span>
                </Link>
              );
            })}
          </div>

          {/* Right: Profile and Logout - Prominently Displayed */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Profile Link */}
            <Link
              to="/settings/profile"
              className="p-2 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
              title={t('navigation.profile')}
            >
              <CogIcon className="w-5 h-5" />
            </Link>
            
            {/* Logout Button */}
            <button
              onClick={handleSignOut}
              className="p-2 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors"
              title={t('navigation.signOut')}
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mobile Menu - Full Navigation (when hamburger is clicked) */}
        {mobileMenuOpen && (
          <div className="mt-4 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Show error message if navigation failed */}
            {navigationError && (
              <div className="px-3 py-2 text-sm text-red-300 bg-red-900 bg-opacity-50 rounded">
                ⚠️ {t('navigation.noFunctionalityAvailable')}
              </div>
            )}
            
            {/* Show navigation modules if available */}
            {navigationModules.length > 0 && navigationModules.map((module) => {
              return (
              <div key={`mobile-module-${module.id}`}>
                {/* Module with children (screens) */}
                <div>
                  <button
                    onClick={() => setMobileSubmenuOpen(mobileSubmenuOpen === (module.path || '') ? null : (module.path || ''))}
                    className={`w-full text-left px-3 py-2 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors flex items-center justify-between text-sm ${
                      mobileSubmenuOpen === (module.path || '') ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-2 min-w-0">
                      {React.createElement(getIconForPath(module.path || '', module.icon), { className: 'w-6 h-6 flex-shrink-0 text-current' })}
                      <span className="truncate">{getTranslatedText(module.display_name, module.translation_key)}</span>
                    </div>
                    {mobileSubmenuOpen === (module.path || '') ? (
                      <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
                    ) : (
                      <ChevronRightIcon className="w-3 h-3 flex-shrink-0" />
                    )}
                  </button>

                  {mobileSubmenuOpen === (module.path || '') && (
                    <div className="ml-4 mt-2 space-y-1">
                      {module.children.map((child) => {
                        // Check if child is a screen or a module
                        if ('screen_code' in child) {
                          // This is a screen
                          return (
                            <Link
                              key={`mobile-child-${child.id}`}
                              to={child.route_path || `/admin/${child.screen_code.toLowerCase().replace(/_/g, '-')}`}
                              className="block px-3 py-2 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors text-sm"
                              onClick={() => {
                                setMobileMenuOpen(false);
                                setMobileSubmenuOpen(null);
                              }}
                            >
                              <div className="flex items-center space-x-2">
                                {React.createElement(getIconForPath(child.icon || 'default'), { className: 'w-5 h-5 flex-shrink-0 text-current' })}
                                <span className="truncate">{getTranslatedText(child.display_name, child.translation_key)}</span>
                              </div>
                            </Link>
                          );
                        } else {
                          // This is a sub-module
                          const subModule = child as any;
                          return (
                            <div key={`mobile-submodule-${module.id}-${subModule.id}`} className="space-y-1">
                              <div className="px-3 py-2 text-sm font-medium text-[var(--color-secondary-text)]">
                                {getTranslatedText(subModule.display_name, subModule.translation_key)}
                              </div>
                              {subModule.children && subModule.children.map((subChild: any) => {
                                if ('screen_code' in subChild) {
                                  // This is a screen in the sub-module
                                  return (
                                    <Link
                                      key={`mobile-subchild-${subChild.id}`}
                                      to={subChild.route_path || `/admin/${subChild.screen_code.toLowerCase().replace(/_/g, '-')}`}
                                      className="block px-6 py-2 rounded text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors text-sm"
                                      onClick={() => {
                                        setMobileMenuOpen(false);
                                        setMobileSubmenuOpen(null);
                                      }}
                                    >
                                      <div className="flex items-center space-x-2">
                                        {React.createElement(getIconForPath(subChild.icon || 'default'), { className: 'w-4 h-4 flex-shrink-0 text-current' })}
                                        <span className="truncate">{getTranslatedText(subChild.display_name, subChild.translation_key)}</span>
                                      </div>
                                    </Link>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          );
                        }
                      })}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </nav>

      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
    </>
  );
}; 
